import React, { useState, useEffect } from 'react';
import './Navbar.css';
import { FaBars, FaTimes, FaSearch, FaArrowRight } from 'react-icons/fa';
import BrandLogo from './BrandLogo';

const navItems = [
    { label: 'About', href: '#about', id: 'about' },
    { label: 'Projects', href: '#projects', id: 'projects' },
    { label: 'Experience', href: '#experience', id: 'experience' },
    { label: 'Skills', href: '#skills', id: 'skills' },
    { label: 'Education', href: '#credentials', id: 'credentials' },
];

const Navbar = ({ onOpenCommandPalette, onOpenResume }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeSection, setActiveSection] = useState('');

    // Scroll listener for glassmorphism header & active section spy
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);

            // If scrolled near bottom of page, highlight contact
            const isBottom = (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 80);
            if (isBottom) {
                setActiveSection('contact');
                return;
            }

            const scrollSections = [...navItems, { id: 'contact' }];
            let current = '';

            for (const item of scrollSections) {
                const el = document.getElementById(item.id);
                if (el) {
                    const rect = el.getBoundingClientRect();
                    // Section is active if top is within upper half of viewport and bottom is below navbar
                    if (rect.top <= window.innerHeight * 0.45 && rect.bottom >= 100) {
                        current = item.id;
                    }
                }
            }

            setActiveSection(current);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on Escape key press or window resize
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && mobileOpen) {
                setMobileOpen(false);
            }
        };

        const handleResize = () => {
            if (window.innerWidth > 992 && mobileOpen) {
                setMobileOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', handleResize);
        };
    }, [mobileOpen]);

    const handleNavClick = (e, href) => {
        e.preventDefault();
        setMobileOpen(false);
        if (href === '#hero') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        const target = document.querySelector(href);
        if (target) {
            const navElement = document.querySelector('.navbar-header');
            const navHeight = navElement ? navElement.offsetHeight : 72;
            const targetPosition = target.getBoundingClientRect().top + window.pageYOffset - navHeight;
            window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        }
    };

    return (
        <header className={`navbar-header ${isScrolled ? 'scrolled' : ''} ${mobileOpen ? 'menu-open' : ''}`}>
            <nav className="navbar-container" aria-label="Main Navigation">
                {/* Brand Mark */}
                <div className="navbar-brand-group">
                    <a
                        href="#hero"
                        className="navbar-brand"
                        onClick={(e) => handleNavClick(e, '#hero')}
                        aria-label="Amit Portfolio Home"
                    >
                        <BrandLogo size={24} className="navbar-brand-logo" />
                        <span className="brand-name">AMIT <span className="brand-surname">KUMAR</span></span>
                        <span className="brand-role-tag">SYSTEMS</span>
                    </a>
                </div>

                {/* Primary Navigation Tabs */}
                <ul className="navbar-links" role="menubar">
                    {navItems.map((item) => {
                        const isActive = activeSection === item.id;
                        return (
                            <li key={item.id} role="none">
                                <a
                                    href={item.href}
                                    role="menuitem"
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`nav-link ${isActive ? 'active' : ''}`}
                                    onClick={(e) => handleNavClick(e, item.href)}
                                >
                                    <span className="nav-label">{item.label}</span>
                                    {isActive && <span className="nav-active-bar" aria-hidden="true" />}
                                </a>
                            </li>
                        );
                    })}
                </ul>

                {/* Action Controls */}
                <div className="navbar-action">
                    <button
                        className="cmd-trigger-btn"
                        onClick={onOpenCommandPalette}
                        aria-label="Open developer command palette"
                        title="Search & Quick Actions (⌘K)"
                    >
                        <FaSearch className="cmd-search-icon" aria-hidden="true" />
                        <span className="cmd-text">Search</span>
                        <span className="cmd-badge" aria-hidden="true">⌘K</span>
                    </button>

                    <a
                        href="#contact"
                        className={`navbar-cta-btn ${activeSection === 'contact' ? 'active' : ''}`}
                        onClick={(e) => handleNavClick(e, '#contact')}
                    >
                        <span>Get in Touch</span>
                        <FaArrowRight className="cta-arrow-icon" aria-hidden="true" />
                    </a>
                </div>

                {/* Mobile Drawer Toggle */}
                <button
                    className="mobile-toggle"
                    onClick={() => setMobileOpen(!mobileOpen)}
                    aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
                    aria-expanded={mobileOpen}
                >
                    {mobileOpen ? <FaTimes aria-hidden="true" /> : <FaBars aria-hidden="true" />}
                </button>
            </nav>

            {/* Mobile Drawer Navigation */}
            <div
                className={`mobile-menu ${mobileOpen ? 'open' : ''}`}
                aria-hidden={!mobileOpen}
            >
                <div className="mobile-menu-inner">
                    <ul className="mobile-links">
                        {navItems.map((item) => (
                            <li key={item.id}>
                                <a
                                    href={item.href}
                                    className={`mobile-nav-link ${activeSection === item.id ? 'active' : ''}`}
                                    onClick={(e) => handleNavClick(e, item.href)}
                                >
                                    {item.label}
                                </a>
                            </li>
                        ))}
                    </ul>

                    <div className="mobile-quick-actions">
                        <button
                            className="mobile-action-btn"
                            onClick={() => { setMobileOpen(false); onOpenCommandPalette(); }}
                        >
                            <span className="cmd-symbol" aria-hidden="true">⌘</span> Command Palette
                        </button>
                        <button
                            className="mobile-action-btn"
                            onClick={() => { setMobileOpen(false); onOpenResume(); }}
                        >
                            Executive CV / Specs
                        </button>
                    </div>

                    <a
                        href="#contact"
                        className="mobile-cta-btn"
                        onClick={(e) => handleNavClick(e, '#contact')}
                    >
                        <span>Contact & Inquiries</span>
                        <FaArrowRight className="cta-arrow-icon" aria-hidden="true" />
                    </a>
                </div>
            </div>
        </header>
    );
};

export default Navbar;
